import {ReelStopPresentationController} from "../ReelStopPresentationController";
import {IReelStateEventData} from "../../../core/reel/ReelState";
import {WalkOfShame} from "../../WalkOfShame";
import {TimelineLite} from "gsap";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {WalkOfShameGameMode} from "../../server/WalkOfShameGameMode";
import {WalkOfShameSymbol} from "../../symbol/WalkOfShameSymbol";
import {WalkOfShameGameConfig} from "../../WalkOfShameGameConfig";
import {Layers} from "../../view/WalkOfShameLayers";
import {WalkOfShameSymbolState} from "../../symbol/WalkOfShameSymbolConfig";
import {WalkOfShameSound} from "../../sound/WalkOfShameSound";

export class ScatterLandingController extends ReelStopPresentationController {

    protected _scatterCounter: number = 0;
    private readonly _numOfScattersToWin: number = 3;
    private readonly _scattersInReel: boolean[] = [false, true, true, true, true];
    private _animations: TimelineLite[] = [];
    private _scatterSymbols: WalkOfShameSymbol[] = [];

    constructor() {
        super("ScatterLandingController");
    }

    protected onReelBounce(data: IReelStateEventData): Promise<any> {
        const scatterPosition: number = WalkOfShame.model.parsedGameData.scatterPositions[data.reelId];

        // #65
        if (WalkOfShameGameConfig.IS_LUCKY_DRAW && data.reelId > 0 && data.reelId < 3) {
            this.turnOnAnticipation(data.reelId - 1);
        }

        // #65
        if (data.reelId === 4 || (WalkOfShameGameConfig.IS_LUCKY_DRAW && data.reelId === 0)) {
            WalkOfShameGameConfig.IS_ANTICIPATING = false;

            WalkOfShame.anticipation.stopAnticipation();

            this._scatterSymbols.forEach((symbol: WalkOfShameSymbol) => {
                symbol.getStateAnimation(WalkOfShameSymbolState.NORMAL);
            });

            this._scatterSymbols = [];
        }

        if (WalkOfShame.model.parsedGameData.nearWinReels[data.reelId + 1] && WalkOfShame.model.parsedGameData.mode === WalkOfShameGameMode.NORMAL && !WalkOfShame.model.parsedGameData.wasFeatureBuy) {
            this.turnOnAnticipation(data.reelId + 1);
        }

        if (scatterPosition === -1) {
            return Promise.resolve();
        }

        this._scatterCounter += 1;

        // any of the FS mode
        if (WalkOfShame.model.parsedGameData.mode.indexOf(WalkOfShameGameMode.FREESPIN) > -1) {
            const fsLeftCtr: number = (WalkOfShame.model.fsLeftCtr + ((this._scatterCounter * 2) - 1));

            WalkOfShame.additionalFS.showAdditionalFS(data.reelId, (scatterPosition - 1), (this._scatterCounter === 1 ? 1 : 2), fsLeftCtr);
        }

        // When there is not enough reel left to win.
        if (!this.isPossibleToWin(data.reelId)) {
            // also cut off the ongoing animations in previous reels.
            this._animations.forEach(a => a.progress(1));
            return Promise.resolve();
        }

        return Promise.resolve(this.playAttentionAnimation(data.reelId, scatterPosition));
    }

    protected reset(): void {
        super.reset();
        this._scatterCounter = 0;
    }

    // override in different scatter winning rules.
    private isPossibleToWin(reelId: number): boolean {
        let counter: number = 0;// how many scatters landed
        let reelsLeft: number = 0; // how many possible reels has scatter left
        WalkOfShame.model.parsedGameData.scatterPositions.forEach((position: number, positionId: number) => {
            if (positionId <= reelId && position > -1) {
                counter++;
            }
            if (positionId > reelId && this._scattersInReel[positionId]) {
                reelsLeft++;
            }
        });

        return (counter + reelsLeft) >= this._numOfScattersToWin;
    }

    private playAttentionAnimation(reelId: number, symId: number): Promise<any> {
        const symbol: WalkOfShameSymbol = WalkOfShame.reels[reelId].getSymbol(symId);

        let anim: TimelineLite = new TimelineLite();
        if (WalkOfShameGameConfig.IS_ANTICIPATING) {
            anim = new TimelineLite().to(this, {duration: 0.001});
        } else {
            anim = symbol.getStateAnimation(WalkOfShameSymbolState.LANDING);
        }

        if (WalkOfShame.model.parsedGameData.mode === WalkOfShameGameMode.NORMAL) {
            WalkOfShameSound.soundPlayer.playTrack({
                name: WalkOfShameSound.S1_LAND[this._scatterCounter - 1]
            });
        } else {
            WalkOfShameSound.soundPlayer.playTrack({
                name: WalkOfShameSound.S1_LAND_FS
            });
        }

        this._animations.push(anim);
        return Promise.resolve(anim).then(() => {
            ArrayHelper.removeFirstMatchElement(this._animations, anim);
        });
    }

    private turnOnAnticipation(reelId: number): void {
        WalkOfShame.anticipation.playAnticipation(reelId);

        if (!WalkOfShameGameConfig.IS_ANTICIPATING) {
            WalkOfShameGameConfig.IS_ANTICIPATING = true;
            WalkOfShame.blackOverlay.showOverlay(0.1, 0.8, Layers.BLACK_OVERLAY_BOTTOM);

            if (WalkOfShameGameConfig.IS_LUCKY_DRAW) {
                for (let reel: number = 4; reel > reelId; reel--) {
                    this.anticipateScatter(reel, WalkOfShame.model.parsedGameData.scatterPositions[reel]);
                }
            } else {
                for (let reel: number = 1; reel < reelId; reel++) {
                    this.anticipateScatter(reel, WalkOfShame.model.parsedGameData.scatterPositions[reel]);
                }
            }
        } else {
            if (WalkOfShameGameConfig.IS_LUCKY_DRAW) {
                this.anticipateScatter(reelId, WalkOfShame.model.parsedGameData.scatterPositions[reelId]);
            } else {
                this.anticipateScatter(reelId - 1, WalkOfShame.model.parsedGameData.scatterPositions[reelId - 1]);
            }
        }
    }

    private anticipateScatter(reelId: number, symId: number): void {
        if (symId > -1) {
            const sym: WalkOfShameSymbol = WalkOfShame.reels[reelId].getSymbol(symId);

            sym.getStateAnimation(WalkOfShameSymbolState.ANTICIPATION);

            this._scatterSymbols.push(sym);
        }
    }
}